import { useState, useEffect, type ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Loader2 } from 'lucide-react';
import { api, API_ENDPOINTS } from '../../lib/api';
import { isVersionValid } from '../../utils/version';
import { ForceUpdateScreen } from './ForceUpdateScreen';
import { MaintenanceScreen } from './MaintenanceScreen';

interface AppConfig {
  minVersion: string;
  forceUpdateEnabled: boolean;
  updateMessage: string;
  maintenanceMode: boolean;
  storeUrl: string;
}

interface VersionGateProps {
  children: ReactNode;
}

export function VersionGate({ children }: VersionGateProps) {
  const [state, setState] = useState<'loading' | 'blocked' | 'maintenance' | 'allowed'>('loading');
  const [config, setConfig] = useState<AppConfig | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function checkVersion() {
      try {
        const { data } = await api.get<AppConfig>(API_ENDPOINTS.APP_CONFIG, {
          params: { platform: Capacitor.getPlatform() },
        });

        if (cancelled) return;

        if (data.forceUpdateEnabled && !isVersionValid(__APP_VERSION__, data.minVersion)) {
          setConfig(data);
          setState('blocked');
        } else if (data.maintenanceMode) {
          setState('maintenance');
        } else {
          setState('allowed');
        }
      } catch {
        // API failure → allow through (don't block users during outages)
        if (!cancelled) setState('allowed');
      }
    }

    checkVersion();
    return () => { cancelled = true; };
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state === 'blocked' && config) {
    return (
      <ForceUpdateScreen
        message={config.updateMessage}
        currentVersion={__APP_VERSION__}
        minVersion={config.minVersion}
        storeUrl={config.storeUrl ?? ''}
      />
    );
  }

  if (state === 'maintenance') {
    return <MaintenanceScreen />;
  }

  return <>{children}</>;
}
